export type CircuitComponentType = 'battery' | 'ammeter' | 'voltmeter' | 'rheostat' | 'resistor' | 'switch';
export type CircuitTerminal = 'positive' | 'negative' | 'left' | 'right' | 'input' | 'output' | 'high' | 'low';

export interface CircuitComponent {
  id: string;
  type: CircuitComponentType;
  resistance?: number;
  closed?: boolean;
  voltage?: number;
}

export interface WireConnection {
  id: string;
  from: { componentId: string; terminal: CircuitTerminal };
  to: { componentId: string; terminal: CircuitTerminal };
}

export interface CircuitValidation {
  valid: boolean;
  energized: boolean;
  warnings: string[];
  errors: string[];
  topology: 'series' | 'parallel' | 'mixed' | 'incomplete' | 'unknown';
  ammeterPlacement: 'series' | 'parallel' | 'disconnected' | 'unknown';
  voltmeterPlacement: 'series' | 'parallel' | 'disconnected' | 'unknown';
  shortCircuit: boolean;
  connectedComponents: string[];
}

function graphFor(components: CircuitComponent[], wires: WireConnection[]) {
  const knownIds = new Set(components.map((component) => component.id));
  const adjacency = new Map<string, Set<string>>();
  const connectedComponentIds = new Set<string>();
  const errors: string[] = [];

  for (const component of components) {
    if (!component.id || adjacency.has(component.id)) errors.push(`Component id '${component.id}' is empty or duplicated.`);
    adjacency.set(component.id, new Set());
  }

  for (const wire of wires) {
    if (!knownIds.has(wire.from.componentId) || !knownIds.has(wire.to.componentId)) {
      errors.push(`Wire '${wire.id}' references an unknown component.`);
      continue;
    }
    if (wire.from.componentId === wire.to.componentId) {
      errors.push(`Wire '${wire.id}' connects a component to itself.`);
      continue;
    }
    // Real apparatus terminals and junction posts commonly accept multiple leads.
    // Repeated terminal use is therefore allowed; only duplicate component IDs,
    // unknown endpoints, and self-connections are rejected here.
    adjacency.get(wire.from.componentId)?.add(wire.to.componentId);
    adjacency.get(wire.to.componentId)?.add(wire.from.componentId);
    connectedComponentIds.add(wire.from.componentId);
    connectedComponentIds.add(wire.to.componentId);
  }

  return { adjacency, connectedComponentIds, errors };
}

function connectedComponentCount(adjacency: Map<string, Set<string>>): number {
  const visited = new Set<string>();
  let count = 0;
  for (const [id, neighbours] of adjacency) {
    if (visited.has(id) || neighbours.size === 0) continue;
    count += 1;
    const stack = [id];
    visited.add(id);
    while (stack.length) {
      const current = stack.pop() as string;
      for (const neighbour of adjacency.get(current) ?? []) {
        if (!visited.has(neighbour)) {
          visited.add(neighbour);
          stack.push(neighbour);
        }
      }
    }
  }
  return count;
}

/**
 * Validate a circuit's graph and instrument placement using terminal-level
 * connections. This is topology validation, not an electrical network solver.
 */
export function validateCircuit(
  components: CircuitComponent[],
  wires: WireConnection[],
  options: { ammeterRange?: number; supplyVoltage?: number } = {},
): CircuitValidation {
  const warnings: string[] = [];
  const { adjacency, connectedComponentIds, errors } = graphFor(components, wires);
  const byType = (type: CircuitComponentType) => components.filter((component) => component.type === type);
  const batteries = byType('battery');
  const ammeters = byType('ammeter');
  const voltmeters = byType('voltmeter');
  const resistors = [...byType('resistor'), ...byType('rheostat')];
  const switches = byType('switch');

  if (batteries.length !== 1) errors.push('The circuit must contain exactly one power supply.');
  if (ammeters.length > 1) warnings.push('Use one ammeter to measure current in this circuit.');
  if (voltmeters.length > 1) warnings.push('Use one voltmeter to measure potential difference across the load.');
  if (resistors.length === 0) errors.push('Add at least one load resistor or rheostat.');

  const switchClosed = switches.length === 0 || switches.every((component) => component.closed !== false);
  const hasOpenSwitch = switches.some((component) => component.closed === false);
  const energized = batteries.length === 1 && switchClosed && errors.length === 0;
  if (hasOpenSwitch) warnings.push('The switch is open; no current should flow.');

  let ammeterPlacement: CircuitValidation['ammeterPlacement'] = 'unknown';
  let voltmeterPlacement: CircuitValidation['voltmeterPlacement'] = 'unknown';
  if (ammeters.length === 1) {
    const neighbours = adjacency.get(ammeters[0].id) ?? new Set<string>();
    const incidentWires = wires.filter((wire) => wire.from.componentId === ammeters[0].id || wire.to.componentId === ammeters[0].id);
    if (incidentWires.length === 0) ammeterPlacement = 'disconnected';
    else if (incidentWires.length === 2 && neighbours.size === 1 && [...neighbours].some((id) => resistors.some((resistor) => resistor.id === id))) ammeterPlacement = 'parallel';
    else ammeterPlacement = 'series';
  }
  if (voltmeters.length === 1) {
    const neighbours = adjacency.get(voltmeters[0].id) ?? new Set<string>();
    const incidentWires = wires.filter((wire) => wire.from.componentId === voltmeters[0].id || wire.to.componentId === voltmeters[0].id);
    if (incidentWires.length === 0) voltmeterPlacement = 'disconnected';
    else if (incidentWires.length === 2 && neighbours.size === 1 && [...neighbours].some((id) => resistors.some((resistor) => resistor.id === id))) voltmeterPlacement = 'parallel';
    else voltmeterPlacement = 'series';
  }

  if (ammeters.length === 1 && ammeterPlacement === 'parallel') {
    warnings.push('Ammeter is connected in parallel. Move it into the main series path to prevent a short circuit.');
  } else if (ammeters.length === 1 && ammeterPlacement === 'disconnected') {
    warnings.push('Ammeter is not connected to the circuit.');
  } else if (ammeters.length === 1 && ammeterPlacement === 'unknown') {
    warnings.push('Connect the ammeter in the main series path.');
  }
  if (voltmeters.length === 1 && voltmeterPlacement === 'series') {
    warnings.push('Voltmeter is connected in series. Connect it across the resistor in parallel.');
  } else if (voltmeters.length === 1 && voltmeterPlacement === 'disconnected') {
    warnings.push('Voltmeter is not connected across the resistor.');
  } else if (voltmeters.length === 1 && voltmeterPlacement === 'unknown') {
    warnings.push('Connect the voltmeter across the load resistor.');
  }

  const supplyVoltage = options.supplyVoltage ?? batteries[0]?.voltage ?? 0;
  const ammeterRange = options.ammeterRange ?? Number.POSITIVE_INFINITY;
  const loadResistance = resistors.reduce((sum, resistor) => sum + (resistor.resistance ?? 0), 0);
  const shortCircuit = supplyVoltage > 0 && connectedComponentIds.size > 0 && (
    loadResistance <= 0 || ammeterPlacement === 'parallel'
  );
  if (shortCircuit) warnings.push('Short circuit detected: disconnect the supply and add load resistance.');
  if (ammeters.length === 1 && loadResistance > 0 && supplyVoltage / loadResistance > ammeterRange) {
    warnings.push('Expected current exceeds the selected ammeter range.');
  }

  const graphHasBranch = [...adjacency.values()].some((neighbours) => neighbours.size > 2);
  const allComponentsConnected = connectedComponentIds.size === components.length;
  if (wires.length > 0 && !allComponentsConnected) warnings.push('Connect every apparatus item into the circuit before closing the key.');
  const topology: CircuitValidation['topology'] = errors.length > 0
    ? 'incomplete'
    : graphHasBranch
      ? 'mixed'
      : connectedComponentCount(adjacency) === 1 && connectedComponentIds.size === components.length
        ? 'series'
        : connectedComponentIds.size === 0
          ? 'unknown'
          : 'incomplete';

  if (wires.length === 0) warnings.push('Connect the components with wires to complete the circuit.');
  return {
    valid: errors.length === 0 && !shortCircuit && allComponentsConnected,
    energized: energized && !shortCircuit && allComponentsConnected
      && (ammeters.length === 0 || ammeterPlacement === 'series')
      && (voltmeters.length === 0 || voltmeterPlacement === 'parallel'),
    warnings: [...new Set(warnings)],
    errors: [...new Set(errors)],
    topology,
    ammeterPlacement,
    voltmeterPlacement,
    shortCircuit,
    connectedComponents: [...connectedComponentIds],
  };
}

/** Snap a dragged terminal to the nearest available terminal within a radius. */
export function findWireSnapTarget<T extends { x: number; y: number; key: string; occupied?: boolean }>(
  pointer: { x: number; y: number },
  candidates: T[],
  radius: number,
): T | null {
  if (!Number.isFinite(radius) || radius < 0) throw new RangeError('radius must be non-negative');
  let nearest: T | null = null;
  let nearestDistance = radius;
  for (const candidate of candidates) {
    if (candidate.occupied) continue;
    const distance = Math.hypot(pointer.x - candidate.x, pointer.y - candidate.y);
    if (distance <= nearestDistance) {
      nearest = candidate;
      nearestDistance = distance;
    }
  }
  return nearest;
}
